import React, { useState, useEffect } from 'react';
import type { FormState, AnalysisFormProps } from '@types';
import { COUNTRIES } from '../../services/locationData';
import AddressSelector from './AddressSelector';
import ReportBuilder from './ReportBuilder';
import ReportHub from './ReportHub';
import FSATForm from './FSATForm';
import EpProjectPlannerForm from './EpProjectPlannerForm';

import ItineraryPlanner from './ItineraryPlanner';
import EventSecurityPlannerForm from './EventSecurityPlannerForm';
import LearningManagementSuite from './LmsSuite';
import Loader from './Loader';
import { ICONS } from './constants';
import { showAppAlert } from '../common/appDialog';
import { validateFacilityProtectionInput } from '../utils/facilityProtection';
import FacilityProtectionFields from './FacilityProtectionFields';

// Helper components defined OUTSIDE the main component to prevent re-mounts
const FormField: React.FC<{ label: string; name: string; value: any; onChange: any; placeholder?: string }> = ({ label, name, value, onChange, placeholder }) => (
    <div>
        <label htmlFor={name} className="block text-sm font-medium text-slate-600 mb-1">{label}</label>
        <textarea
            id={name}
            name={name}
            value={value || ''}
            onChange={onChange}
            rows={4}
            className="w-full bg-white border border-slate-300 rounded-md p-2 focus:ring-2 focus:ring-blue-500 focus:outline-none transition-colors"
            placeholder={placeholder}
        />
    </div>
);

const InputField: React.FC<{ label: string; name: string; value: any; onChange: any; type?: string, placeholder?: string }> = ({ label, name, value, onChange, type = 'text', placeholder }) => (
    <div>
        <label htmlFor={name} className="block text-sm font-medium text-slate-600 mb-1">{label}</label>
        <input
            type={type}
            id={name}
            name={name}
            value={value || ''}
            onChange={onChange}
            placeholder={placeholder}
            className="w-full bg-white border border-slate-300 rounded-md p-2 focus:ring-2 focus:ring-blue-500 focus:outline-none transition-colors"
        />
    </div>
);

const CheckboxGroup: React.FC<{
    label: string;
    name: string;
    options: string[];
    value: string[];
    onChange: any;
}> = ({ label, name, options, value, onChange }) => {
    const selected = Array.isArray(value) ? value : [];
    return (
        <fieldset>
            <legend className="block text-sm font-medium text-slate-600 mb-2">{label}</legend>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {options.map(option => (
                    <label key={option} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
                        <input
                            type="checkbox"
                            checked={selected.includes(option)}
                            onChange={() => onChange({
                                target: {
                                    name,
                                    value: selected.includes(option)
                                        ? selected.filter(item => item !== option)
                                        : [...selected, option]
                                }
                            })}
                            className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                        />
                        {option}
                    </label>
                ))}
            </div>
        </fieldset>
    );
};

// --- MAIN EXPORTED COMPONENT ---

const AnalysisForm: React.FC<AnalysisFormProps> = (props) => {
    const { suiteName, workflowName, formData, isLoading, error, onSubmit, onFormChange, onRequestHelp, workflowId } = props;
    const [waypoints, setWaypoints] = useState<string[]>(Array.isArray(formData.waypoints) ? formData.waypoints : []);

    // Effect to sync waypoints with parent form data - Guarded against loops
    useEffect(() => {
        // Only sync if these are different AND we have actual waypoints or the parent has different data
        // Also avoid syncing for FSAT as it doesn't use waypoints in this manner
        if (workflowId === 'facility_risk_assessment') return;

        const currentWaypoints = Array.isArray(formData.waypoints) ? formData.waypoints : [];
        if (JSON.stringify(currentWaypoints) !== JSON.stringify(waypoints)) {
            onFormChange({ target: { name: 'waypoints', value: waypoints } });
        }
    }, [waypoints, onFormChange, formData.waypoints, workflowId]);

    const handleAddWaypoint = () => {
        setWaypoints([...waypoints, '']);
    };

    const handleWaypointChange = (index: number, value: string) => {
        const newWaypoints = [...waypoints];
        newWaypoints[index] = value;
        setWaypoints(newWaypoints);
    };

    const handleRemoveWaypoint = (index: number) => {
        const newWaypoints = waypoints.filter((_, i) => i !== index);
        setWaypoints(newWaypoints);
    };

    // 1. LMS Suite Handling
    if (suiteName === 'Learning Management Suite' || suiteName === 'Learning Management Suite (LMS)') {
        return <LearningManagementSuite {...props} />;
    }

    // 2. Complex Workflow Components (Entire screen takeovers)
    
    if (workflowName === 'Facility Security Assessment Tool (FSAT)' || workflowId === 'facility_risk_assessment') {
        return <FSATForm 
            formData={formData as any} 
            onFormChange={onFormChange} 
            planDataForAudit={props.planDataForAudit} 
            onAuditComplete={props.onAuditComplete} 
            onSubmit={onSubmit}
            inline={props.inline}
            srmProject={props.srmProject || undefined}
            srmModule={props.srmModule || undefined}
        />;
    }

    if (workflowName === 'EP Project Planner') {
        return <EpProjectPlannerForm 
            formData={formData} 
            onFormChange={onFormChange} 
            onInitiateThreatAnalysis={props.onInitiateThreatAnalysis!} 
            onAutoRunThreatAnalysis={props.onAutoRunThreatAnalysis}
            onInitializeOperation={props.onInitializeOperation} 
            isBackgroundLoading={props.isBackgroundLoading}
            inline={props.inline} 
            onNavigate={props.onNavigate}
        />;
    }

    if (workflowName === 'Event Security Plan') {
        return <EventSecurityPlannerForm 
            formData={formData}
            onFormChange={onFormChange}
            onSubmit={onSubmit}
            isLoading={isLoading}
            onRequestHelp={() => onRequestHelp(suiteName, workflowName)}
        />;
    }

    if (workflowName === 'Create Composite Report') {
        return <ReportBuilder isLoading={isLoading} onSubmit={onSubmit} inline={props.inline} />;
    }

    if (workflowName === 'Composite Analytics Dashboard') {
        return (
            <ReportHub 
                isLoading={isLoading} 
                onSubmit={onSubmit} 
                onNavigate={props.onNavigate} 
                initialTab="analytics"
                currentOrganization={props.currentOrganization}
                currentUser={props.currentUser}
            />
        );
    }

    if (workflowName === 'Itinerary Risk Workflow') {
         return (
            <form onSubmit={onSubmit} className="space-y-4">
                <ItineraryPlanner 
                    formData={formData}
                    onFormChange={onFormChange}
                />
                <div className="flex justify-end">
                     <button type="submit" disabled={isLoading} className="bg-blue-600 text-white py-2 px-6 rounded-md hover:bg-blue-700 disabled:bg-slate-400">
                        {isLoading ? 'Analyzing...' : 'Generate Analysis'}
                    </button>
                </div>
            </form>
         );
    }

    // 3. Standard Form Logic (for simpler workflows)
    
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (workflowId === 'facility_asset_protection') {
            const missing = validateFacilityProtectionInput(formData);
            if (missing.length > 0) {
                void showAppAlert(`Complete these required fields: ${missing.join(', ')}.`);
                return;
            }
        }
        onSubmit(e);
    };

    // Render logic for standard input fields based on workflow name
    const renderFields = () => {
        // Use the reliable workflowId for logic
        const { workflowId } = props;

        // --- START INSERTION: Key Intelligence Questions for Integrated Threat Analysis ---
        if (workflowId === 'integrated_threat_analysis') {
            return (
                 <div className="space-y-4">
                    <InputField label="Scope of Analysis" name="scope" value={formData.scope} onChange={onFormChange} placeholder="e.g., Upcoming election security, supply chain risks" />
                    <div className="p-4 bg-slate-50 border rounded-lg">
                        <h4 className="font-semibold text-slate-700 mb-2">Target Location</h4>
                        <AddressSelector prefix="location" formData={formData} onFormChange={onFormChange} />
                    </div>
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                         <FormField 
                            label="Key Intelligence Questions (Optional)" 
                            name="kiq" 
                            value={formData.kiq} 
                            onChange={onFormChange} 
                            placeholder="Guide the AI's focus. For example: 'What is the risk of protest activity near the main port?' or 'Assess the reliability of the local power grid.'"
                        />
                         <p className="text-xs text-blue-600 mt-2">By providing specific questions, you can tailor the analysis to your exact needs, similar to tasking a professional intelligence analyst.</p>
                    </div>
                 </div>
            );
        }
        // --- END INSERTION ---

        // Facility and Asset Protection Plan
        if (workflowId === 'facility_asset_protection') {
            return <FacilityProtectionFields formData={formData} onFormChange={onFormChange} />;
        }

        // HSSE Workflows
        if (workflowId === 'hsse_compliance_briefing') {
             return (
                 <div className="space-y-4">
                    <div className="p-4 bg-slate-50 border rounded-lg">
                        <h4 className="font-semibold text-slate-700 mb-2">Jurisdiction & Location</h4>
                        <AddressSelector prefix="hsse_comp_address" formData={formData} onFormChange={onFormChange} />
                    </div>
                    <InputField label="Business Type / Industry" name="hsse_comp_business_type" value={formData.hsse_comp_business_type} onChange={onFormChange} placeholder="e.g., Retail, Manufacturing, Office" />
                 </div>
             );
        }
        if (workflowId === 'hsse_incident_report') {
             return (
                 <div className="space-y-4">
                    <div className="p-4 bg-slate-50 border rounded-lg">
                        <h4 className="font-semibold text-slate-700 mb-2">Incident Location</h4>
                        <AddressSelector prefix="hsse_incident_address" formData={formData} onFormChange={onFormChange} />
                    </div>
                    <FormField label="Incident Description" name="hsse_incident_description" value={formData.hsse_incident_description} onChange={onFormChange} placeholder="Describe what happened..." />
                 </div>
             );
        }

        // Security & EP Operations Risk Analysis - Granular Location Update
        if (workflowId === 'ep_ops_risk_analysis') {
            return (
                <div className="space-y-4">
                     <div className="p-4 bg-slate-50 border rounded-lg">
                        <h4 className="font-semibold text-slate-700 mb-2">Target Area of Operations</h4>
                        <p className="text-xs text-slate-500 mb-4">Select a country for a general report, or pinpoint a specific city/address for a granular tactical analysis.</p>
                        <AddressSelector prefix="ep_ops_location" formData={formData} onFormChange={onFormChange} />
                    </div>
                </div>
            );
        }

        // Country Analysis (GG Only)
        if (workflowId === 'country_geopolitical_analysis' || workflowId === 'comprehensive_country_geopolitical_risk_analysis') {
            const INTEL_MODULES = [
                { icon: '🏛️', label: 'PMESII-PT Framework', desc: '8-domain geopolitical assessment' },
                { icon: '🎯', label: 'CARVER Matrix', desc: 'Target criticality & exposure' },
                { icon: '⚖️', label: 'Governance Diagram', desc: 'WGI scores & institutional map' },
                { icon: '📈', label: 'Economic Indicators', desc: 'World Bank GDP, inflation, trade' },
                { icon: '🗺️', label: 'Geospatial Threat Map', desc: 'Live risk event heatmap' },
                { icon: '🛡️', label: 'Travel Advisory', desc: 'US State Dept & FCDO levels' },
                { icon: '🧬', label: 'COA Analysis', desc: 'MLCOA / MDCOA scenario planning' },
                { icon: '📋', label: 'Leadership Profiles', desc: 'Key figures & stability outlook' },
            ];

            return (
                <div className="space-y-5">
                    {/* Country Selector */}
                    <div>
                        <label htmlFor="country" className="block text-sm font-semibold text-slate-700 mb-2">
                            🌍 Target Country
                        </label>
                        <select
                            id="country"
                            name="country"
                            value={formData.country || ''}
                            onChange={onFormChange}
                            className="w-full bg-white border-2 border-slate-200 rounded-xl p-3 text-slate-800 font-medium focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none transition-all hover:border-slate-300 text-base"
                        >
                            <option value="">Select a country to analyse...</option>
                            {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </div>

                    {/* Intelligence Scope Panel */}
                    <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-blue-50 overflow-hidden">
                        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-white/60">
                            <div className="flex items-center gap-2">
                                <span className="text-blue-600 font-bold text-sm uppercase tracking-wide">Intelligence Report Coverage</span>
                            </div>
                            <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2 py-1 rounded-full">
                                ⏱ Est. 45–90 sec generation
                            </span>
                        </div>
                        <div className="grid grid-cols-2 gap-px bg-slate-200/60 p-px">
                            {INTEL_MODULES.map((mod) => (
                                <div
                                    key={mod.label}
                                    className="flex items-start gap-3 bg-white/80 backdrop-blur-sm p-3 hover:bg-blue-50/60 transition-colors"
                                >
                                    <span className="text-xl flex-shrink-0 mt-0.5">{mod.icon}</span>
                                    <div>
                                        <p className="text-xs font-bold text-slate-800 leading-tight">{mod.label}</p>
                                        <p className="text-xs text-slate-500 mt-0.5 leading-tight">{mod.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="px-4 py-2.5 bg-amber-50 border-t border-amber-200 flex items-center gap-2">
                            <span className="text-amber-600 text-sm">⚡</span>
                            <p className="text-xs text-amber-800">
                                <strong>Live OSINT:</strong> Analysis is grounded with real-time World Bank, WHO, ACAPS, and travel advisory data at time of generation.
                            </p>
                        </div>
                    </div>
                </div>
            );
        }

        
        // EP Route Analysis
        if (workflowId === 'advanced_route_analysis_ep_plan') {
            return (
                <div className="space-y-4">
                    <InputField label="Route Name" name="route_name" value={formData.route_name} onChange={onFormChange} placeholder="e.g., Airport to Hotel" />
                    <div className="grid grid-cols-2 gap-4">
                        <InputField label="Start Date" name="start_date" value={formData.start_date} onChange={onFormChange} type="date" />
                        <InputField label="End Date" name="end_date" value={formData.end_date} onChange={onFormChange} type="date" />
                    </div>
                    
                    <div className="bg-slate-50 border border-slate-200 p-4 rounded-lg space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Start Point (Origin)</label>
                            <AddressSelector prefix="trm_origin" formData={formData} onFormChange={onFormChange} />
                        </div>
                        
                        {/* Dynamic Waypoints */}
                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-slate-700">Intermediate Stops / Waypoints</label>
                            {waypoints.map((wp, index) => (
                                <div key={index} className="flex gap-2 items-center">
                                    <input
                                        type="text"
                                        value={wp}
                                        onChange={(e) => handleWaypointChange(index, e.target.value)}
                                        placeholder={`Stop ${index + 1} (e.g. Gas Station, Meeting Point)`}
                                        className="flex-grow p-2 border border-slate-300 rounded-md"
                                    />
                                    <button type="button" onClick={() => handleRemoveWaypoint(index)} className="text-red-500 hover:text-red-700 p-2">
                                        {React.cloneElement(ICONS.trash as any, { className: 'h-5 w-5' })}
                                    </button>
                                </div>
                            ))}
                            <button type="button" onClick={handleAddWaypoint} className="text-sm text-blue-600 font-medium hover:underline flex items-center">
                                + Add Stop
                            </button>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">End Point (Destination)</label>
                            <AddressSelector prefix="trm_destination" formData={formData} onFormChange={onFormChange} />
                        </div>
                    </div>
                </div>
            );
        }

        // EP Threat Analysis — expanded with USSS/DS Principal Vulnerability Assessment fields
        if (workflowId === 'ep_threat_analysis') {
            return (
                <div className="space-y-5">
                    {/* Principal Profile */}
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-3">
                        <h4 className="text-xs font-bold text-slate-600 uppercase tracking-widest">Principal Profile</h4>
                        <div className="grid grid-cols-2 gap-3">
                            <InputField label="Principal Name / VIP" name="principal_name" value={formData.principal_name} onChange={onFormChange} placeholder="Full name" />
                            <InputField label="Title / Role" name="principal_title" value={formData.principal_title} onChange={onFormChange} placeholder="CEO, Ambassador, etc." />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-sm font-medium text-slate-600 mb-1">Public Profile / Visibility</label>
                                <select name="ep_ta_digital_footprint" value={formData.ep_ta_digital_footprint || ''} onChange={onFormChange}
                                    className="w-full bg-white border border-slate-300 rounded-md p-2 focus:ring-2 focus:ring-blue-500 focus:outline-none">
                                    <option value="">Select level</option>
                                    <option value="Low">Low — Limited public presence</option>
                                    <option value="Medium">Medium — Known in sector</option>
                                    <option value="High">High — Frequent media coverage</option>
                                    <option value="Critical">Critical — Celebrity / political figure</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-600 mb-1">Family / Household Exposure</label>
                                <select name="ep_ta_family_exposure" value={formData.ep_ta_family_exposure || ''} onChange={onFormChange}
                                    className="w-full bg-white border border-slate-300 rounded-md p-2 focus:ring-2 focus:ring-blue-500 focus:outline-none">
                                    <option value="">Select level</option>
                                    <option value="None">None — Not publicly known</option>
                                    <option value="Low">Low — Minimal exposure</option>
                                    <option value="Medium">Medium — Some public exposure</option>
                                    <option value="High">High — Family publicly visible</option>
                                </select>
                            </div>
                        </div>
                        <FormField label="Pattern of Life (PoL)" name="ep_ta_pattern_of_life" value={formData.ep_ta_pattern_of_life} onChange={onFormChange}
                            placeholder="Regular routes, venues, time patterns, commute details, recurring schedules..." />
                    </div>

                    {/* Location & Known Threats */}
                    <div className="p-4 bg-slate-50 border rounded-lg">
                        <h4 className="font-semibold text-slate-700 mb-2">Location of Operations</h4>
                        <AddressSelector prefix="location" formData={formData} onFormChange={onFormChange} />
                    </div>
                    <FormField label="Known Threat Actors / Context" name="threat_summary" value={formData.threat_summary} onChange={onFormChange}
                        placeholder="Describe any known threats, hostile actors, or specific concerns..." />

                    {/* Adversary Intent × Capability Matrix */}
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3">
                        <h4 className="text-xs font-bold text-amber-800 uppercase tracking-widest">Adversary Assessment Matrix (USSS Standard)</h4>
                        <p className="text-xs text-amber-700">Score each adversary category 1 (Negligible) – 5 (Imminent/Capable)</p>
                        <div className="grid grid-cols-2 gap-3">
                            {[
                                { label: 'Adversary Intent Score (1–5)', name: 'ep_ta_adversary_intent', placeholder: '1=None, 5=Imminent' },
                                { label: 'Adversary Capability Score (1–5)', name: 'ep_ta_adversary_capability', placeholder: '1=No capability, 5=Highly capable' },
                            ].map(f => (
                                <div key={f.name}>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">{f.label}</label>
                                    <input type="number" min={1} max={5} name={f.name} value={formData[f.name] || ''} onChange={onFormChange}
                                        placeholder={f.placeholder}
                                        className="w-full bg-white border border-slate-300 rounded-md p-2 focus:ring-2 focus:ring-amber-500 focus:outline-none" />
                                </div>
                            ))}
                        </div>
                        <FormField label="Lone Actor / Insider Threat Assessment" name="ep_ta_lone_actor_assessment" value={formData.ep_ta_lone_actor_assessment} onChange={onFormChange}
                            placeholder="Known disgruntled employees, obsessive individuals, online threatening communications against the principal..." />
                    </div>

                    {/* Movement Patterns */}
                    <FormField label="Movement Patterns" name="movement_patterns" value={formData.movement_patterns} onChange={onFormChange}
                        placeholder="Regular travel routes, office locations, residential address general area, gym / social venues..." />
                </div>
            );
        }

        // Staff Protection Plan — multi-staff personnel security
        if (workflowId === 'staff_protection_plan') {
            return (
                <div className="space-y-5">
                    {/* Staff / Group Identification */}
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-3">
                        <h4 className="text-xs font-bold text-slate-600 uppercase tracking-widest">Staff / Group Identification</h4>
                        <div className="grid grid-cols-2 gap-3">
                            <InputField
                                label="Staff Group / Principal Name"
                                name="principal_name"
                                value={formData.principal_name}
                                onChange={onFormChange}
                                placeholder="e.g., Logistics Team Alpha, John Smith"
                            />
                            <div>
                                <label className="block text-sm font-medium text-slate-600 mb-1">Required Protection Level</label>
                                <select
                                    name="protection_level"
                                    value={formData.protection_level || ''}
                                    onChange={onFormChange}
                                    className="w-full bg-white border border-slate-300 rounded-md p-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                >
                                    <option value="">Select protection level</option>
                                    <option value="Low">Low — General awareness &amp; monitoring</option>
                                    <option value="Standard">Standard — Security escort &amp; check-ins</option>
                                    <option value="Enhanced">Enhanced — Close protection &amp; secure transport</option>
                                    <option value="Critical">Critical — Armed protection &amp; full advance work</option>
                                </select>
                            </div>
                        </div>
                        <FormField
                            label="Staff Roles &amp; Exposure Levels"
                            name="spp_staff_roles"
                            value={formData.spp_staff_roles}
                            onChange={onFormChange}
                            placeholder="Describe the team composition and individual exposure: e.g., 2x Field Engineers (high exposure), 1x Site Manager (medium), 3x Admin Staff (low)..."
                        />
                    </div>

                    {/* Operating Location */}
                    <div className="p-4 bg-slate-50 border rounded-lg">
                        <h4 className="font-semibold text-slate-700 mb-2">Deployment Location</h4>
                        <p className="text-xs text-slate-500 mb-3">Provide as much location detail as possible — city and country are mandatory.</p>
                        <AddressSelector prefix="location" formData={formData} onFormChange={onFormChange} />
                    </div>

                    {/* Deployment Dates */}
                    <div className="grid grid-cols-2 gap-4">
                        <InputField label="Deployment Start Date" name="spp_start_date" value={formData.spp_start_date} onChange={onFormChange} type="date" />
                        <InputField label="Deployment End Date" name="spp_end_date" value={formData.spp_end_date} onChange={onFormChange} type="date" />
                    </div>

                    {/* Threat Context */}
                    <FormField
                        label="Known Threat Context"
                        name="threat_summary"
                        value={formData.threat_summary}
                        onChange={onFormChange}
                        placeholder="Describe any known threats, hostile actors, crime trends, civil unrest, or specific security concerns for the deployment area..."
                    />

                    {/* Communication & Duty of Care */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                        <h4 className="text-xs font-bold text-blue-800 uppercase tracking-widest">Communication &amp; Duty of Care (ISO 31030)</h4>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-sm font-medium text-slate-600 mb-1">Check-in Frequency</label>
                                <select
                                    name="spp_checkin_frequency"
                                    value={formData.spp_checkin_frequency || ''}
                                    onChange={onFormChange}
                                    className="w-full bg-white border border-slate-300 rounded-md p-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                >
                                    <option value="">Select frequency</option>
                                    <option value="Hourly">Hourly (High-risk environment)</option>
                                    <option value="Every 4 hours">Every 4 hours</option>
                                    <option value="Twice daily">Twice daily (Morning &amp; Evening)</option>
                                    <option value="Daily">Daily (Standard)</option>
                                    <option value="As-required">As-required (Managed events only)</option>
                                </select>
                            </div>
                            <InputField
                                label="Emergency Contact / Crisis Line"
                                name="spp_emergency_contact"
                                value={formData.spp_emergency_contact}
                                onChange={onFormChange}
                                placeholder="e.g., +1 800 555 0100 (24/7)"
                            />
                        </div>
                        <FormField
                            label="Special Instructions / Restrictions"
                            name="spp_special_instructions"
                            value={formData.spp_special_instructions}
                            onChange={onFormChange}
                            placeholder="No-go zones, restricted areas, local customs to observe, medical conditions, specific organizational security policies..."
                        />
                        <p className="text-xs text-blue-700">
                            <strong>ISO 31030 Duty of Care:</strong> The AI will generate a structured plan covering pre-deployment briefings,
                            in-country monitoring protocols, and emergency evacuation procedures.
                        </p>
                    </div>
                </div>
            );
        }


        if (workflowId === 'travel_security_brief') {
            return (
                <div className="space-y-4">
                     <InputField label="Traveler Name" name="trm_traveler_name" value={formData.trm_traveler_name} onChange={onFormChange} />
                     <div className="grid grid-cols-2 gap-4">
                        <InputField label="Start Date" name="trm_start_date" value={formData.trm_start_date} onChange={onFormChange} type="date" />
                        <InputField label="End Date" name="trm_end_date" value={formData.trm_end_date} onChange={onFormChange} type="date" />
                     </div>
                     <div className="p-4 bg-slate-50 border rounded-lg">
                        <h4 className="font-semibold text-slate-700 mb-2">Destination</h4>
                        <AddressSelector prefix="trm_destination" formData={formData} onFormChange={onFormChange} />
                     </div>
                </div>
            );
        }

        // Audit and Compliance (New Workflow)
        if (workflowId === 'audit_and_compliance') {
             return (
                 <div className="space-y-4">
                    <div className="p-4 bg-slate-50 border rounded-lg">
                        <h4 className="font-semibold text-slate-700 mb-2">Facility / Program Location</h4>
                        <AddressSelector prefix="audit_location" formData={formData} onFormChange={onFormChange} />
                    </div>
                    <InputField label="Audit Type / Compliance Standard" name="audit_standard" value={formData.audit_standard} onChange={onFormChange} placeholder="e.g., ISO 22301, OSHA, Internal Security Policy" />
                    <FormField label="Specific Focus Areas or Known Issues" name="audit_focus" value={formData.audit_focus} onChange={onFormChange} placeholder="Describe any particular areas of concern or previously identified non-conformities..." />
                 </div>
             );
        }

        // Threat Group Network Analysis (OSINT)
        if (workflowId === 'threat_group_network_analysis') {
            return (
                <div className="space-y-4">
                    <InputField label="Target Group Name / Entity" name="entity_name" value={formData.entity_name} onChange={onFormChange} placeholder="e.g., Lazarus Group, Local Cartel" />
                    <FormField label="Known Activities / Indicators" name="indicators" value={formData.indicators} onChange={onFormChange} placeholder="Describe recent activities, social media handles, or known associates..." />
                    <div className="p-4 bg-slate-50 border rounded-lg">
                        <h4 className="font-semibold text-slate-700 mb-2">Primary Area of Interest</h4>
                        <AddressSelector prefix="location" formData={formData} onFormChange={onFormChange} />
                    </div>
                </div>
            );
        }

        // Daily Micro Report
        if (workflowId === 'daily_micro_report') {
            return (
                <div className="space-y-4">
                    <div className="p-4 bg-slate-50 border rounded-lg">
                        <h4 className="font-semibold text-slate-700 mb-2">Report Location Scope</h4>
                        <AddressSelector prefix="location" formData={formData} onFormChange={onFormChange} />
                    </div>
                    <InputField label="Reporting Period" name="date_range" value={formData.date_range} onChange={onFormChange} placeholder="e.g., Last 24 Hours, This Weekend" />
                    <FormField label="Specific Intelligence Priorities" name="priorities" value={formData.priorities} onChange={onFormChange} placeholder="What specific events or threats are we watching? (e.g., Port strikes, political rallies)" />
                </div>
            );
        }

        // Default Fallback for generic text analysis prompts
        return (
             <FormField label="Analysis Context / Prompt" name="srm_context" value={formData.srm_context} onChange={onFormChange} placeholder="Provide details for the analysis..." />
        );
    };
    
    return (
        <div className={`space-y-4 ${props.inline ? '' : ''}`}>
            <form onSubmit={handleSubmit} className={`space-y-6 bg-white rounded-lg border shadow-sm animate-fade-in ${props.inline ? 'p-0 border-none bg-transparent shadow-none' : 'p-6 border-slate-200'}`}>
                <div className="flex justify-between items-start mb-4">
                    <div>
                        <h4 className="text-lg font-semibold text-slate-700">Input Parameters</h4>
                        <p className="text-sm text-slate-500">Provide context for the Insights engine.</p>
                    </div>
                    <button type="button" onClick={() => onRequestHelp(suiteName, workflowName)} className="text-blue-600 text-sm font-medium hover:underline">Need Help?</button>
                </div>
                
                {renderFields()}

                <div className="flex justify-end pt-4 border-t border-slate-100">
                    <button type="submit" disabled={isLoading} className="bg-blue-600 text-white py-2 px-6 rounded-md hover:bg-blue-700 disabled:bg-slate-400 flex items-center transition-colors font-semibold">
                        {isLoading ? <Loader /> : 'Generate Analysis'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default AnalysisForm;
